// @todo enable the following disabled rules see OPENTOK-31136 for more info
import { v4 as uuid } from 'uuid';

import logEvents from '../logEvents';
import RumorMessage from './RumorMessage';
import * as RumorMessageTypes from './RumorMessageTypes';
import SocketError from './SocketError';
import { messages as socketCloseMessages, codes as socketCloseCodes } from '../socketCloseCodes';
import AutoClearingSet from '../../helpers/AutoClearingSet';

export default ({
  EventEmitter,
  ReconnectableSocket,
  logging,
  allocateId,
}) => class RumorSocket extends EventEmitter {
  static CONNECTING = ReconnectableSocket.CONNECTING;
  static OPEN = ReconnectableSocket.OPEN;
  static CLOSING = ReconnectableSocket.CLOSING;
  static CLOSED = ReconnectableSocket.CLOSED;

  url;
  id;

  _opened = false;
  _socket;
  _reconnectAttempts = 0;
  _socketID = uuid();
  _receivedTransactionIDs = new AutoClearingSet(30000, 10000); // 30 seconds of ttl, check every 10 seconds
  _notifyDisconnectAddress;
  _enableReconnection;
  _signalingNetworkQualityEstimator;

  get readyState() {
    return (this._socket ?
      this._socket.readyState :
      ReconnectableSocket.CONNECTING
    );
  }

  get reconnecting() { return this._opened && this._socket && this._socket.reconnecting; }

  // Messages which have not yet been acknowledged
  _pendingMessages = new Map();

  _logger = logging(`RumorSocket:${allocateId()}`);

  constructor({
    messagingURL,
    notifyDisconnectAddress,
    connectionId,
    sessionId,
    enableReconnection,
    pingThreshold = 2000,
    disconnectThreshold = ((enableReconnection ? 3 : 25) * pingThreshold) - 100,
    reconnectMaxDuration = enableReconnection ? 30000 : 0,
    p2pEnabled,
    signalingNetworkQualityEstimator,
  }) {
    super();

    logEvents({
      logger: this._logger,
      obj: this,
      eventNames: [
        'open',
        'message',
        'error',
        'reconnecting',
        'reconnectAttempt',
        'reconnectFailure',
        'reconnected',
        'close',
      ],
    });

    this.url = messagingURL;
    this.id = connectionId;

    this._notifyDisconnectAddress = notifyDisconnectAddress;
    this._enableReconnection = enableReconnection;

    this._signalingNetworkQualityEstimator = signalingNetworkQualityEstimator;

    this.on('error', (socketError) => {
      this._logger.error(socketError.message);
    });

    try {
      this._socket = new ReconnectableSocket({
        url: () => (!this._enableReconnection ? this.url : [
          this.url,
          this.url.indexOf('?') >= 0 ? '&' : '?',
          `socketId=${this._socketID}`,
          this._socket && this._socket.reconnecting ? '&reconnect=true' : '',
          `&attempt=${uuid()}`,
        ].join('')),
        pingThreshold,
        disconnectThreshold,
        reconnectMaxDuration,
        connectionId,
        sessionId,
        p2pEnabled,
      });
    } catch (e) {
      this._logger.error(e);
      this.emit('error', new SocketError(socketCloseCodes.CLOSE_CONNECT_EXCEPTION));
    }

    const onOpen = () => {
      this._opened = true;
      this._socket.send({
        data: RumorMessage.Connect(this.id, notifyDisconnectAddress).serialize(),
        retryAfterReconnect: true,
        expectAck: false,
      });
      this.emit('open', this.id);
    };

    this._socket.once('open', onOpen);

    this._socket.on('message', event => this._receiveMessage(event));

    this._socket.once('close', (closeEvent) => {
      this._logger.debug(`ReconnectableSocket closed (code: ${closeEvent.code})`);

      this._clearPendingMessages();

      // We don't emit an error for these close codes because they are normal and TIMEOUT has a
      // dedicated error and we would duplicate it here.
      const { CLOSE_NORMAL, CLOSE_GOING_AWAY, CLOSE_TIMEOUT } = socketCloseCodes;

      if ([CLOSE_NORMAL, CLOSE_GOING_AWAY, CLOSE_TIMEOUT].indexOf(closeEvent.code) === -1) {
        this.emit('error', new SocketError(
          closeEvent.code
        ));
      }

      this.emit('close', closeEvent);
    });

    this._socket.on('error', ({ code }) => this.emit('error', new SocketError(code)));

    this._socket.on('needsPing', () => {
      this._socket.send({
        data: RumorMessage.Ping().serialize(),
        retryAfterReconnect: true,
        expectAck: true,
      });
    });

    this._socket.on('missedAck', () => this._signalingNetworkQualityEstimator.addMissedAck());

    this._socket.on('reconnecting', () => {
      if (!this._opened) { return; } // ignore reconnect events when we haven't opened yet
      this._clearPendingMessagesWhichShouldntRetry();
      this.emit('reconnecting');
      this._reconnectAttempts = 0;
    });

    this._socket.on('reconnectAttempt', () => {
      if (!this._opened) { return; } // ignore reconnect events when we haven't opened yet
      this._reconnectAttempts += 1;

      // TODO: Although this should be ok if the socket is already closed since ReconnectableSocket
      // will just drop it due to retryAfterReconnect=false, it should be tested.
      this._socket.send({
        data: RumorMessage.Disconnect('1').serialize(),
        retryAfterReconnect: false,
        expectAck: false,
      });

      this.emit('reconnectAttempt');
    });

    this._socket.on('reconnected', () => {
      if (!this._opened) {
        onOpen();
        return;
      } // ignore reconnect events when we haven't opened yet
      // Resend pending messages (unacknowledged messages)
      this._pendingMessages.forEach((msg) => {
        this._socket.send({
          data: msg.rumorMessage.serialize(),
          retryAfterReconnect: true,
          expectAck: true,
        });
      });

      this.emit('reconnected');
    });

    this._socket.on('reconnectFailure', () => {
      if (!this._opened) { return; } // ignore reconnect events when we haven't opened yet
      this._logger.debug('Reconnecting failed as connectivity was not restored within ' +
        `${reconnectMaxDuration}ms`);

      // TODO: This was picked when porting over the old code, but we should be able to set use a
      // better code and message here.
      this.emit('reconnectFailure', new SocketError());
    });
  }

  _clearPendingMessagesWhichShouldntRetry() {
    this._pendingMessages.forEach((message, transactionId) => {
      if (!message.retryAfterReconnect) {
        this._pendingMessages.delete(transactionId);
        const error = new Error('Not connected.');
        error.code = 500;
        message.completion?.(error);
      }
    });
  }

  _clearPendingMessages() {
    this._pendingMessages.forEach((message) => {
      const error = new Error('Not connected.');
      error.code = 500;
      message.completion?.(error);
    });

    this._pendingMessages.clear();
  }

  _sendAck(msg) {
    this._socket.send({
      data: RumorMessage.Status([msg.fromAddress], {
        'TRANSACTION-ID': msg.headers['TRANSACTION-ID'],
        'X-TB-FROM-ADDRESS': this.id,
      }).serialize(),
      retryAfterReconnect: true,
      expectAck: false,
    });
  }

  _receiveMessage(messageEvent) {
    const msg = RumorMessage.deserialize(messageEvent.data);
    // Emit an error message if we fail to deserialize the message. For now we are only interested
    // in tracking this error and not interfeering with the normal flow.
    if (!msg || !Object.values(RumorMessageTypes).includes(msg.type)) {
      const error = {
        errorMsg: 'Deserialize RumorMessage failure.',
      };
      this.emit('error#message', error);
    }
    if (msg.type === RumorMessageTypes.PONG) {
      return;
    }

    let msgData;
    if (msg.data) {
      try {
        msgData = JSON.parse(msg.data);
      } catch (e) {
        this._logger.error(`Failed to parse JSON with error ${e}`);
      }
    }
    if (msgData?.content?.type === 'data') {
      this._logger.spam('Received:', msg);
    } else {
      this._logger.debug('Received:', msg);
    }

    if (msg.transactionId) {
      // remove pending message
      const pendingMessage = this._pendingMessages.get(msg.transactionId);
      if (pendingMessage) {
        this._pendingMessages.delete(msg.transactionId);
        this._logger.debug('Marking', msg.transactionId, ' as received');
        this._signalingNetworkQualityEstimator.addReceivedAck(pendingMessage.timestamp);
        pendingMessage.completion?.(undefined, msg);
      }

      if (msg.type !== RumorMessageTypes.STATUS) {
        // 1) ack it!
        this._sendAck(msg);

        // Have we seen this transaction before?
        const transactionSeenBefore = this._receivedTransactionIDs.has(msg.transactionId);
        this._receivedTransactionIDs.add(msg.transactionId);
        if (transactionSeenBefore) {
          // We've handled this transactionId before, but the ACK
          // must have been lost. That's ok, we've told the server
          // so we can just ignore this message now.
          return;
        }
      }
    }

    this.emit('message', msg);
  }

  publish(topics, message, headers, retryAfterReconnect, completion = () => {}) {
    // TODO: Instead of completion, sending a message should return an event emitter which emits:
    // - sent: if connected, emitted on next tick, if reconnecting, emitted on reconnected
    // - ack: received ack for this message from server
    // - error: won't receive ack (not connected / dropped)
    // Also should have .status obtained from ReconnectableSocket.send

    const rumorMessage = RumorMessage.Publish(topics, message, headers);

    // We always use retryAfterReconnect=false on ReconnectableSocket because rumor does additional
    // retries based on the ack status of messages which ReconnectableSocket doesn't know about,
    // and we don't want these retry strategies to conflict.
    const status = this._socket.send({
      data: rumorMessage.serialize(),
      retryAfterReconnect: false,
      expectAck: true,
      transactionId: rumorMessage.transactionId,
    });

    if (status === 'dropped' && !retryAfterReconnect) {
      const error = new Error('Not connected.');
      error.code = 500;
      completion(error);
      return;
    }

    this._pendingMessages.set(rumorMessage.transactionId, {
      rumorMessage,
      retryAfterReconnect,
      completion,
      timestamp: Date.now(),
    });
  }

  cancel(transactionId) {
    this._pendingMessages.delete(transactionId);
    this._receivedTransactionIDs.add(transactionId);
    this._socket.cancel(transactionId);
  }

  subscribe(topics) {
    this._socket.send({
      data: RumorMessage.Subscribe(topics).serialize(),
      retryAfterReconnect: true,
      expectAck: false,
    });
  }

  unsubscribe(topics) {
    this._socket.send({
      data: RumorMessage.Unsubscribe(topics).serialize(),
      retryAfterReconnect: true,
      expectAck: false,
    });
  }

  disconnect() {
    if (this.readyState === RumorSocket.OPEN) {
      const message = RumorMessage.Disconnect();
      this._socket.send({
        data: message.serialize(),
        retryAfterReconnect: true,
        expectAck: false,
      });
    }

    if (
      this._socket.readyState !== ReconnectableSocket.CLOSED &&
      this._socket.readyState !== ReconnectableSocket.CLOSING
    ) {
      const { CLOSE_NORMAL } = socketCloseCodes;
      this._socket.close(CLOSE_NORMAL, socketCloseMessages[CLOSE_NORMAL]);
    }
  }

  status(toAddress, transactionId) {
    this._socket.send({
      data: RumorMessage.Status(toAddress, {
        'TRANSACTION-ID': transactionId,
        'X-TB-FROM-ADDRESS': this.id,
      }).serialize(),
      retryAfterReconnect: true,
      expectAck: true,
    });
  }

  reconnectRetriesCount() { return this._reconnectAttempts; }
  messageQueueSize() { return this._pendingMessages.size; }

  // This could do with some reconciliation. Also with logger name now.
  get socketID() { return this._socketID; }
};
